/**
 * The quest creator's review on a certificate (20261021120000): 1–5 stars,
 * quick tags from a fixed list, and the comment. Pure — shared by the
 * approval modal, the task page, the certificate page and the adapters.
 */

/** The tags, in display order. The database stores them sorted, so the order lives here. */
export const REVIEW_TAGS = ["on_time", "quality", "communication", "would_hire_again"] as const;
export type ReviewTag = (typeof REVIEW_TAGS)[number];

export const MAX_REVIEW_NOTE = 500;

export const MAX_RATING = 5;

/** The review lock: changes are refused this long after the first save. */
export const REVIEW_EDIT_DAYS = 14;

/** Stars below this are hidden from anyone but the worker and the creator. */
export const PUBLIC_RATING_MIN = 3;

export interface CertificateReview {
  /** 1–5, or null for no stars. */
  rating: number | null;
  tags: ReviewTag[];
  note: string;
}

export const EMPTY_REVIEW: CertificateReview = { rating: null, tags: [], note: "" };

/** The review changed after the editor loaded it; the save was refused, not merged (40001). */
export class ReviewConflictError extends Error {
  constructor() {
    super("this review changed since it was loaded");
    this.name = "ReviewConflictError";
  }
}

export function isReviewTag(tag: string): tag is ReviewTag {
  return (REVIEW_TAGS as readonly string[]).includes(tag);
}

/** Known tags only, deduped, in display order. Unknown tags from an older or newer server are dropped. */
export function orderTags(tags: readonly string[] | null | undefined): ReviewTag[] {
  const set = new Set(tags ?? []);
  return REVIEW_TAGS.filter((t) => set.has(t));
}

/** A whole 1–5 rating, or null for anything else (none, out of range, fractional). */
export function normalizeRating(rating: unknown): number | null {
  return typeof rating === "number" && Number.isInteger(rating) && rating >= 1 && rating <= MAX_RATING
    ? rating
    : null;
}

/** Stars the server publishes to anyone holding the link (get_certificate). */
export function isPublicRating(rating: number | null): boolean {
  return rating !== null && rating >= PUBLIC_RATING_MIN;
}

/** A radio group's arrow step: from no stars, forward lands on 1 and back on 5; clamped to 1–5. */
export function stepRating(rating: number | null, step: 1 | -1): number {
  const from = rating ?? (step > 0 ? 0 : MAX_RATING + 1);
  return Math.min(MAX_RATING, Math.max(1, from + step));
}

/** What gets sent: a whole 1–5 rating or none, ordered tags, a trimmed note. */
export function normalizeReview(review: CertificateReview): CertificateReview {
  return {
    rating: normalizeRating(review.rating),
    tags: orderTags(review.tags),
    note: review.note.trim(),
  };
}

/** Nothing to save: an empty review clears what was there. */
export function isEmptyReview(review: CertificateReview): boolean {
  const n = normalizeReview(review);
  return n.rating === null && n.tags.length === 0 && n.note === "";
}

export function sameReview(a: CertificateReview, b: CertificateReview): boolean {
  const x = normalizeReview(a);
  const y = normalizeReview(b);
  return x.rating === y.rating && x.note === y.note && x.tags.join() === y.tags.join();
}

/** The first save + the window; null when there has been no review yet. */
export function reviewEditableUntil(firstAt: string | null, days = REVIEW_EDIT_DAYS): string | null {
  if (!firstAt) return null;
  return new Date(new Date(firstAt).getTime() + days * 86_400_000).toISOString();
}

/** Past its window. A review never given is never locked. The caller passes the clock. */
export function isReviewLocked(editableUntil: string | null, now: Date): boolean {
  return editableUntil !== null && now.getTime() > new Date(editableUntil).getTime();
}

/** Toggle a tag, keeping display order. */
export function toggleTag(tags: readonly ReviewTag[], tag: ReviewTag): ReviewTag[] {
  return tags.includes(tag) ? tags.filter((t) => t !== tag) : orderTags([...tags, tag]);
}
